import type { TimelineBlock, TimelineEntry } from './turn-grouping'

/** The message a reader opened session details from. */
export interface SessionDetailsFocus {
  /** The carrier's id for the message, as the thread timeline names it. */
  messageId: string | null
  /** When the message happened. */
  at: string
}

/** What the drawer scrolls to and flashes: one turn, or one row outside a turn. */
export type FocusTarget =
  | { kind: 'turn'; turnId: string }
  | { kind: 'entry'; entryId: string }

function entryId(entry: TimelineEntry): string {
  return entry.log.id
}

/**
 * The turn whose inbound or reply is the focused message. A session without
 * turn stamps (a v2 session) focuses the first row written at or after the
 * message.
 */
export function focusTarget(
  blocks: TimelineBlock[],
  focus: SessionDetailsFocus | null | undefined,
): FocusTarget | null {
  if (!focus) return null
  if (focus.messageId) {
    for (const block of blocks) {
      if (block.kind !== 'turn') continue
      const { stamp } = block.turn
      if (
        stamp.inboundExternalMessageId === focus.messageId ||
        stamp.outboundExternalMessageId === focus.messageId
      ) {
        return { kind: 'turn', turnId: stamp.turnId }
      }
    }
  }
  const at = new Date(focus.at).getTime()
  for (const block of blocks) {
    if (block.kind === 'turn') {
      const received = new Date(block.turn.stamp.receivedAt).getTime()
      const sent = new Date(block.turn.stamp.sentAt).getTime()
      if (at >= received - 1000 && at <= sent + 1000) {
        return { kind: 'turn', turnId: block.turn.stamp.turnId }
      }
      continue
    }
    if (new Date(block.entry.log.createdAt).getTime() >= at) {
      return { kind: 'entry', entryId: entryId(block.entry) }
    }
  }
  return null
}
